// @tg-seq: 10265
// @tg-api-binding: 10224-1,10672-1,10673-1,10265-1
/**
 * Event Submission Master Record
 *
 * Purpose: Master screen for the Universal Event Submission family. Left
 * panel lists submitted Universal Events (reuses the existing Event
 * Submission controller at 10673/1, @manage=5 -- does NOT duplicate it).
 * Right panel shows a read-only view of the selected event record plus
 * its Universal Timeline (status history), with the ability to add a new
 * timeline entry (status picker scoped to the event's Lifecycle Type).
 *
 * Reuses existing endpoints rather than duplicating them:
 *   - 10224/1 (@manage=18): Lifecycle Type names, for display lookup
 *   - 10672/1 (@manage=5): Event Type names, for display lookup (scoped by Lifecycle_Type_ID)
 *   - 10672/2 (@manage=5): Submission Question definitions (incl. Options_Json) for the
 *     selected event's Event Type -- used to rationalize single_choice/multi_choice/
 *     fk_lookup answers (raw Option_Value -> human-readable Option_Label) in the
 *     Submission Details view, since the submitted payload stores raw option values.
 *   - 10673/1 (@manage=5): List submitted Universal Events (master list)
 *   - 10265/1 (@manage=5): List Universal Timeline entries for the selected event
 *   - 10265/1 (@manage=7): Status picker (available statuses for the event's Lifecycle Type)
 *   - 10265/1 (@manage=2): Create a new Universal Timeline entry for the selected event
 *
 * Table: SSCCO.Active_Universal_Event (view), SSCCO.Active_Universal_Timeline (new)
 * Controller: SSCCO.sp_Active_Universal_Event_Controller, SSCCO.sp_Active_Universal_Timeline_Controller
 * Created: 2026-10-23
 */

// === AUTO-GENERATED START: API_ENDPOINTS ===
/**
 * API Endpoint Registry - Component 10265
 * Generated from Router_Registry (TSX_Seq_Num = 10265)
 * Auto-synced on build - DO NOT EDIT MANUALLY
 *
 * Router 1: tsx_10265_1.py (TSX_COMPONENT)
 *   Controller: SSCCO.sp_Active_Universal_Timeline_Controller
 *   Endpoint: /api/tsx/10265/1
 *   Usage: Universal Timeline entries CRUD, owned by Event Submission Master Record screen
 *
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const API_ENDPOINTS = {
    MAIN: '/api/tsx/10265/1',
};
// === AUTO-GENERATED END: API_ENDPOINTS ===

import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequestBySeq } from '../../utils/apiService';
import { getTsxFileName } from '../../utils/componentIdentity';
import { useAuth } from '../../utils/useAuth';

// =============================================
// Types
// =============================================

interface UniversalEvent {
    universal_event_uuid: string;
    lifecycle_type_id: number;
    owner_organization_uuid: string;
    submission_orgcode?: string;
    submission_pfuser_uuid?: string;
    source_system?: string;
    eventtype_id?: number;
    event_submission_payload?: string;
    current_status_id?: number;
    current_status_timestamp?: string;
    last_utl_record?: number;
    last_timeline_status_id?: number;
    last_timeline_posted_at?: string;
    isactive: number | boolean;
    created_date?: string;
    modified_date?: string;
}

interface TimelineEntry {
    utl_record: number;
    universal_event_uuid: string;
    lifecycle_status_id: number;
    status_timestamp: string;
    status_note?: string;
    status_shortcode?: string;
    status_timeline_header?: string;
    is_inferred?: boolean | number;
}

interface StatusOption {
    lifecycle_status_id: number;
    status_shortcode?: string;
    status_timeline_header?: string;
    status_sort_order?: number;
    lifecycle_state?: string;
}

interface LifecycleTypeOption {
    lifecycle_type_id: number;
    type_code: string;
    type_name: string;
}

interface EventTypeOption {
    event_type_id: number;
    event_type: string;
}

interface SubmissionPhoto {
    question: string;
    url: string;
}

interface ParsedSubmission {
    photos: SubmissionPhoto[];
    // value is a plain string for single-answer questions, or a string[] for
    // questions with Allow_Multiple_Answers -- kept as an array (rather than
    // joined into one comma-separated string) so the UI can render each
    // answer as its own structured list item instead of one run-on line.
    fields: { question: string; value: string | string[] }[];
}

// Definitions fetched from 10672/2 (Submission Question CRUD) -- only the
// fields needed to rationalize choice-style answers back to their labels.
interface SubmissionQuestionOption {
    option_value: string;
    option_label: string;
}

interface SubmissionQuestionDef {
    submission_question: string;
    field_type: string;
    options_json?: string | null;
}

// question label -> (raw option_value -> human-readable option_label)
type OptionLookup = Record<string, Record<string, string>>;

function buildOptionLookup(questions: SubmissionQuestionDef[]): OptionLookup {
    const lookup: OptionLookup = {};
    questions.forEach((q) => {
        const isChoiceLike =
            q.field_type === 'single_choice' ||
            q.field_type === 'multi_choice' ||
            q.field_type === 'fk_lookup';
        if (!isChoiceLike || !q.options_json) return;
        try {
            const options: SubmissionQuestionOption[] = JSON.parse(q.options_json);
            const valueToLabel: Record<string, string> = {};
            options.forEach((opt) => {
                valueToLabel[opt.option_value] = opt.option_label;
            });
            lookup[q.submission_question] = valueToLabel;
        } catch {
            // Malformed Options_Json -- raw values will display unresolved
        }
    });
    return lookup;
}

// Matches blob-storage/photo-upload URLs ending in a common image extension
// (with optional query string), e.g. the event-submissions container paths.
const IMAGE_URL_REGEX = /^https?:\/\/\S+\.(?:jpe?g|png|gif|webp|bmp|heic|heif)(?:\?.*)?$/i;

function parseSubmissionPayload(
    payload?: string,
    optionLookup: OptionLookup = {},
): ParsedSubmission {
    const result: ParsedSubmission = { photos: [], fields: [] };
    if (!payload) return result;

    let parsed: Record<string, unknown>;
    try {
        parsed = JSON.parse(payload);
    } catch {
        result.fields.push({ question: 'Raw Payload', value: payload });
        return result;
    }

    Object.entries(parsed).forEach(([question, value]) => {
        if (typeof value === 'string' && IMAGE_URL_REGEX.test(value)) {
            result.photos.push({ question, url: value });
            return;
        }
        const valueToLabel = optionLookup[question];
        if (Array.isArray(value)) {
            const urls = value.filter(
                (v): v is string => typeof v === 'string' && IMAGE_URL_REGEX.test(v),
            );
            if (urls.length > 0 && urls.length === value.length) {
                urls.forEach((url, idx) =>
                    result.photos.push({
                        question: value.length > 1 ? `${question} (${idx + 1})` : question,
                        url,
                    }),
                );
                return;
            }
            const resolved = value.map((v) =>
                typeof v === 'string' ? (valueToLabel?.[v] ?? v) : String(v),
            );
            // Keep multiple answers as a list so they render structured below;
            // a single-entry array collapses to a plain string for display.
            result.fields.push({
                question,
                value: resolved.length === 1 ? resolved[0] : resolved,
            });
            return;
        }
        if (typeof value === 'boolean') {
            result.fields.push({ question, value: value ? 'Yes' : 'No' });
            return;
        }
        if (typeof value === 'string' && valueToLabel?.[value]) {
            result.fields.push({ question, value: valueToLabel[value] });
            return;
        }
        result.fields.push({ question, value: String(value) });
    });

    return result;
}

// =============================================
// Component
// =============================================

const Self: React.FC = () => {
    const { pfUser, activeOrganizationUUID, userOrganizations } = useAuth();

    // Local organization filter for this screen's Universal Events list. Defaults
    // to the global header selection, but is independently overridable here (this
    // screen is most often opened as an overlay from the Phone Tool, where the
    // header's org context may be set to "All Organizations"). Reuses the already-
    // loaded userOrganizations from Redux (via useAuth()) -- no new API call.
    const [selectedOrgUuid, setSelectedOrgUuid] = useState<string>(
        activeOrganizationUUID && activeOrganizationUUID !== 'ALL' ? activeOrganizationUUID : 'ALL',
    );
    const isAllOrgsActive = !selectedOrgUuid || selectedOrgUuid === 'ALL';

    const [events, setEvents] = useState<UniversalEvent[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<UniversalEvent | null>(null);
    const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
    const [statusOptions, setStatusOptions] = useState<StatusOption[]>([]);
    const [lifecycleTypes, setLifecycleTypes] = useState<LifecycleTypeOption[]>([]);
    const [eventTypes, setEventTypes] = useState<EventTypeOption[]>([]);
    const [submissionQuestions, setSubmissionQuestions] = useState<SubmissionQuestionDef[]>([]);

    const [loadingEvents, setLoadingEvents] = useState(false);
    const [loadingTimeline, setLoadingTimeline] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<string | null>(null);

    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newStatusId, setNewStatusId] = useState<number | ''>('');
    const [newStatusNote, setNewStatusNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [lightboxPhoto, setLightboxPhoto] = useState<SubmissionPhoto | null>(null);

    // -----------------------------------------
    // Load lookups (lifecycle types) once
    // -----------------------------------------
    useEffect(() => {
        (async () => {
            try {
                const res = await apiRequestBySeq<{
                    success: boolean;
                    data: LifecycleTypeOption[];
                }>(10224, 1, {
                    manage: 18,
                    organization_uuid: '00000000-0000-0000-0000-000000000000',
                });
                setLifecycleTypes(res?.data || []);
            } catch {
                // Non-fatal -- display falls back to raw IDs
            }
        })();
    }, []);

    // -----------------------------------------
    // Load Universal Events (master list)
    // -----------------------------------------
    const loadEvents = useCallback(async () => {
        setLoadingEvents(true);
        setError(null);
        try {
            const payload: Record<string, unknown> = { manage: 5 };
            if (!isAllOrgsActive) {
                payload.owner_organization_uuid = selectedOrgUuid;
            }
            const res = await apiRequestBySeq<{ success: boolean; data: UniversalEvent[] }>(
                10673,
                1,
                payload,
            );
            setEvents(res?.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load Universal Events');
        } finally {
            setLoadingEvents(false);
        }
    }, [selectedOrgUuid, isAllOrgsActive]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    // -----------------------------------------
    // Load Event Types for the selected event's Lifecycle Type (display names)
    // -----------------------------------------
    useEffect(() => {
        if (!selectedEvent) return;
        (async () => {
            try {
                const res = await apiRequestBySeq<{ success: boolean; data: EventTypeOption[] }>(
                    10672,
                    1,
                    {
                        manage: 5,
                        lifecycle_type_id: selectedEvent.lifecycle_type_id,
                    },
                );
                setEventTypes(res?.data || []);
            } catch {
                setEventTypes([]);
            }
        })();
    }, [selectedEvent]);

    // -----------------------------------------
    // Load Submission Question definitions (incl. Options_Json) for the
    // selected event's Event Type -- used to rationalize choice/fk_lookup
    // answers in the payload back to their human-readable option labels.
    // -----------------------------------------
    useEffect(() => {
        if (!selectedEvent?.eventtype_id) {
            setSubmissionQuestions([]);
            return;
        }
        (async () => {
            try {
                const res = await apiRequestBySeq<{
                    success: boolean;
                    data: SubmissionQuestionDef[];
                }>(10672, 2, {
                    manage: 5,
                    event_type_id: selectedEvent.eventtype_id,
                    isactive: 1,
                });
                setSubmissionQuestions(res?.data || []);
            } catch {
                setSubmissionQuestions([]);
            }
        })();
    }, [selectedEvent]);

    // -----------------------------------------
    // Load Universal Timeline for the selected event
    // -----------------------------------------
    const loadTimeline = useCallback(async (universalEventUuid: string) => {
        setLoadingTimeline(true);
        try {
            const res = await apiRequestBySeq<{ success: boolean; data: TimelineEntry[] }>(
                10265,
                1,
                {
                    manage: 5,
                    universal_event_uuid: universalEventUuid,
                },
            );
            setTimeline(res?.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load Universal Timeline');
        } finally {
            setLoadingTimeline(false);
        }
    }, []);

    useEffect(() => {
        if (selectedEvent) {
            loadTimeline(selectedEvent.universal_event_uuid);
        } else {
            setTimeline([]);
        }
    }, [selectedEvent, loadTimeline]);

    // -----------------------------------------
    // Status picker (for the Add Timeline Entry dialog)
    // -----------------------------------------
    const openAddDialog = useCallback(async () => {
        if (!selectedEvent) return;
        setNewStatusId('');
        setNewStatusNote('');
        setAddDialogOpen(true);
        try {
            const res = await apiRequestBySeq<{ success: boolean; data: StatusOption[] }>(
                10265,
                1,
                {
                    manage: 7,
                    lifecycle_type_id: selectedEvent.lifecycle_type_id,
                },
            );
            setStatusOptions(res?.data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load status options');
        }
    }, [selectedEvent]);

    const handleAddTimelineEntry = async () => {
        if (!selectedEvent || !newStatusId) return;
        setSaving(true);
        try {
            await apiRequestBySeq(10265, 1, {
                manage: 2,
                universal_event_uuid: selectedEvent.universal_event_uuid,
                lifecycle_type_id: selectedEvent.lifecycle_type_id,
                owner_organization_uuid: selectedEvent.owner_organization_uuid,
                reporting_pfuser_uuid: pfUser?.user_uuid || null,
                lifecycle_status_id: newStatusId,
                status_note: newStatusNote || null,
            });
            setAddDialogOpen(false);
            setSnackbar('Timeline entry added.');
            await loadTimeline(selectedEvent.universal_event_uuid);
            await loadEvents();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add timeline entry');
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------
    // Display lookups
    // -----------------------------------------
    const lifecycleTypeName = useCallback(
        (id?: number) => {
            const match = lifecycleTypes.find((t) => t.lifecycle_type_id === id);
            return match
                ? `${match.type_code} — ${match.type_name}`
                : id != null
                  ? `Type #${id}`
                  : '—';
        },
        [lifecycleTypes],
    );

    const eventTypeName = useCallback(
        (id?: number) => {
            const match = eventTypes.find((t) => t.event_type_id === id);
            return match ? match.event_type : id != null ? `Event Type #${id}` : '—';
        },
        [eventTypes],
    );

    const optionLookup = useMemo(
        () => buildOptionLookup(submissionQuestions),
        [submissionQuestions],
    );

    const submission = useMemo(
        () => parseSubmissionPayload(selectedEvent?.event_submission_payload, optionLookup),
        [selectedEvent, optionLookup],
    );

    // =============================================
    // Render
    // =============================================
    return (
        <Box sx={{ p: 2, display: 'flex', gap: 2, height: '100%' }}>
            {/* LEFT: Master list of Universal Events */}
            <Card sx={{ width: 380, flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, overflow: 'auto' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1,
                        }}
                    >
                        <Typography variant="h6">Universal Events</Typography>
                        <Button size="small" startIcon={<RefreshIcon />} onClick={loadEvents}>
                            Refresh
                        </Button>
                    </Box>
                    <FormControl size="small" fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="universal-events-org-filter-label">Organization</InputLabel>
                        <Select
                            labelId="universal-events-org-filter-label"
                            label="Organization"
                            value={selectedOrgUuid}
                            onChange={(e: SelectChangeEvent) => setSelectedOrgUuid(e.target.value)}
                        >
                            <MenuItem value="ALL">All Organizations</MenuItem>
                            {userOrganizations.map((org) => (
                                <MenuItem key={org.organization_uuid} value={org.organization_uuid}>
                                    {org.dba_name || org.organization_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {loadingEvents ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : events.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 2 }}>
                            No submitted events found.
                        </Typography>
                    ) : (
                        <List disablePadding>
                            {events.map((ev) => (
                                <ListItemButton
                                    key={ev.universal_event_uuid}
                                    selected={
                                        selectedEvent?.universal_event_uuid ===
                                        ev.universal_event_uuid
                                    }
                                    onClick={() => setSelectedEvent(ev)}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    <ListItemText
                                        primary={lifecycleTypeName(ev.lifecycle_type_id)}
                                        secondary={
                                            <>
                                                {ev.source_system || 'unknown source'}
                                                {ev.created_date
                                                    ? ` · ${new Date(ev.created_date).toLocaleString()}`
                                                    : ''}
                                            </>
                                        }
                                    />
                                    {ev.current_status_id ? (
                                        <Chip
                                            size="small"
                                            label={`Status ${ev.current_status_id}`}
                                        />
                                    ) : null}
                                </ListItemButton>
                            ))}
                        </List>
                    )}
                </CardContent>
            </Card>

            {/* RIGHT: Selected event record + Universal Timeline */}
            <Card sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                    {!selectedEvent ? (
                        <Typography color="text.secondary">
                            Select a Universal Event to view its record and timeline.
                        </Typography>
                    ) : (
                        <Stack spacing={2}>
                            {/* Event Record (view of the Universal Event, via the existing Event Submission controller) */}
                            <Box>
                                <Typography variant="h6">Event Record</Typography>
                                <Divider sx={{ mb: 1 }} />
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                    <Chip
                                        label={lifecycleTypeName(selectedEvent.lifecycle_type_id)}
                                    />
                                    <Chip
                                        label={eventTypeName(selectedEvent.eventtype_id)}
                                        variant="outlined"
                                    />
                                    {selectedEvent.source_system && (
                                        <Chip
                                            label={selectedEvent.source_system}
                                            variant="outlined"
                                        />
                                    )}
                                    {selectedEvent.current_status_id && (
                                        <Chip
                                            color="primary"
                                            label={`Current Status: ${selectedEvent.current_status_id}`}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Universal_Event_UUID: {selectedEvent.universal_event_uuid}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Submitted:{' '}
                                    {selectedEvent.created_date
                                        ? new Date(selectedEvent.created_date).toLocaleString()
                                        : '—'}
                                </Typography>
                                {submission.photos.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Photos ({submission.photos.length})
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {submission.photos.map((photo, idx) => (
                                                <Box
                                                    key={`${photo.url}-${idx}`}
                                                    component="button"
                                                    type="button"
                                                    onClick={() => setLightboxPhoto(photo)}
                                                    title={photo.question}
                                                    sx={{
                                                        p: 0,
                                                        width: 96,
                                                        height: 96,
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1,
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                        bgcolor: 'transparent',
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={photo.url}
                                                        alt={photo.question}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {submission.fields.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                            Submission Details
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                rowGap: 0.5,
                                                columnGap: 2,
                                            }}
                                        >
                                            {submission.fields.map((f) => (
                                                <React.Fragment key={f.question}>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        {f.question}
                                                    </Typography>
                                                    {Array.isArray(f.value) ? (
                                                        <Stack spacing={0.5}>
                                                            {f.value.map((v, idx) => (
                                                                <Box
                                                                    key={idx}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'flex-start',
                                                                        gap: 0.75,
                                                                        px: 1,
                                                                        py: 0.5,
                                                                        bgcolor: 'action.hover',
                                                                        borderRadius: 1,
                                                                    }}
                                                                >
                                                                    <Chip
                                                                        label={idx + 1}
                                                                        size="small"
                                                                        sx={{
                                                                            height: 18,
                                                                            minWidth: 18,
                                                                            '& .MuiChip-label': {
                                                                                px: 0.75,
                                                                                fontSize: 11,
                                                                            },
                                                                        }}
                                                                    />
                                                                    <Typography variant="body2">
                                                                        {v}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {f.value}
                                                        </Typography>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            {/* Universal Timeline */}
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="h6">Universal Timeline</Typography>
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={openAddDialog}
                                    >
                                        Add Timeline Entry
                                    </Button>
                                </Box>
                                <Divider sx={{ mb: 1 }} />
                                {loadingTimeline ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : timeline.length === 0 ? (
                                    <Typography color="text.secondary">
                                        No timeline entries yet for this event.
                                    </Typography>
                                ) : (
                                    <List disablePadding>
                                        {timeline.map((entry) => (
                                            <ListItemText
                                                key={entry.utl_record}
                                                sx={{ mb: 1 }}
                                                primary={`${entry.status_shortcode || entry.lifecycle_status_id} — ${
                                                    entry.status_timeline_header || ''
                                                }`}
                                                secondary={
                                                    <>
                                                        {new Date(
                                                            entry.status_timestamp,
                                                        ).toLocaleString()}
                                                        {entry.status_note
                                                            ? ` · ${entry.status_note}`
                                                            : ''}
                                                    </>
                                                }
                                            />
                                        ))}
                                    </List>
                                )}
                            </Box>
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {/* Add Timeline Entry Dialog */}
            <Dialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Timeline Entry</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="status-select-label">Status</InputLabel>
                            <Select
                                labelId="status-select-label"
                                label="Status"
                                value={newStatusId}
                                onChange={(e: SelectChangeEvent<number | ''>) =>
                                    setNewStatusId(e.target.value as number)
                                }
                            >
                                {statusOptions.map((opt) => (
                                    <MenuItem
                                        key={opt.lifecycle_status_id}
                                        value={opt.lifecycle_status_id}
                                    >
                                        {opt.status_shortcode} — {opt.status_timeline_header} (
                                        {opt.lifecycle_state})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Note (optional)"
                            multiline
                            minRows={2}
                            value={newStatusNote}
                            onChange={(e) => setNewStatusNote(e.target.value)}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!newStatusId || saving}
                        onClick={handleAddTimelineEntry}
                    >
                        {saving ? 'Saving…' : 'Add Entry'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Photo Lightbox */}
            <Dialog
                open={!!lightboxPhoto}
                onClose={() => setLightboxPhoto(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>{lightboxPhoto?.question}</DialogTitle>
                <DialogContent>
                    {lightboxPhoto && (
                        <Box
                            component="img"
                            src={lightboxPhoto.url}
                            alt={lightboxPhoto.question}
                            sx={{
                                width: '100%',
                                maxHeight: '70vh',
                                objectFit: 'contain',
                                display: 'block',
                                mx: 'auto',
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    {lightboxPhoto && (
                        <Button
                            component="a"
                            href={lightboxPhoto.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Open Original
                        </Button>
                    )}
                    <Button onClick={() => setLightboxPhoto(null)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={3000}
                onClose={() => setSnackbar(null)}
                message={snackbar}
            />
            {error && (
                <Snackbar open autoHideDuration={5000} onClose={() => setError(null)}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Snackbar>
            )}
        </Box>
    );
};

Self.displayName = getTsxFileName(
    import.meta.url,
); /* scr_tgc_10265_event_submission_master_record */
export default Self;
